<template>
	<div class="py-6 border-b last:border-b-0 last:pb-0">
		<div class="flex w-full justify-center my-6 space-x-2">
			<div class="flex flex-col justify-center w-[80px] h-[80px] shrink-0">
				<img :src="item.variant?.imageUrl || item.item.images?.find((image) => image.isThumbnail)?.url" class="max-w-[80px] max-h-[80px] object-contain rounded" />
			</div>
			<div class="w-full">
				<div class="flex w-full">
					<div class="text-ihh-midnight-gray w-full max-w-52 mr-4">
						<div class="font-semibold text-base text-ihh-dark-salmon">{{ item.item.name }}</div>
						<div class="text-xs">{{ item.item.brand?.name }}</div>
					</div>
					<div class="text-ihh-midnight-gray text-lg">${{ formattedPrice }}</div>
				</div>
				<div v-if="item?.optionSelections?.length" class="mt-2">
					<div v-for="option in item?.optionSelections" :key="option.id" class="text-xs text-ihh-midnight-gray">
						<span class="font-semibold">{{ `${option.option_name}: ` }}</span>
						<span class="text-ihh-gray">{{ option.option_value }}</span>
					</div>
				</div>
				<div v-if="item.variant" class="text-ihh-midnight-gray font-semibold pt-2 px-2 text-xs">
					<div v-for="option in item.variant.optionValues" :key="option.label">
						<span>{{ `${option.optionDisplayName}: ` }}</span>
						<span class="text-ihh-gray font-normal">{{ option.label }}</span>
					</div>
					<div v-if="XCoverProducts.includes(item.item?.bcId)">
						<span>{{ `Covered Product Code: ` }}</span>
						<span class="text-ihh-gray font-normal">{{ item.optional?.coveredProductCode }}</span>
					</div>
					<div v-if="XCoverProducts.includes(item.item?.bcId)">
						<span>{{ `Covered Product: ` }}</span>
						<span class="text-ihh-gray font-normal">{{ item.optional.productName }}</span>
					</div>
				</div>
			</div>
		</div>

		<div class="flex w-full justify-between text-sm text-ihh-midnight-gray">
			<div class="flex w-32 justify-around max-w-[120px]">
				<button
					@click="emitReduceQty"
					class="shadow shadow-ihh-midnight-gray/20 m-auto flex w-7 h-7 rounded-full drop-shadow-2xl shadow-black bg-white items-center justify-center text-lg font-medium"
				>
					-
				</button>
				<div class="m-auto text-base font-medium">{{ item.quantity }}</div>
				<button
					@click="emitIncreaseQty"
					class="shadow shadow-ihh-midnight-gray/20 m-auto flex w-7 h-7 rounded-full drop-shadow-2xl shadow-black bg-white items-center justify-center text-lg font-medium"
				>
					+
				</button>
			</div>
			<div>
				<button
					@click="emitRemoveItem"
					class="flex group w-9 h-9 p-2 justify-around z-10 hover:bg-ihh-slate-gray bg-ihh-gray/20 rounded-full font-bold text-xl text-ihh-midnight-gray"
				>
					<div class="m-auto items-center flex w-full justify-center">
						<div class="rotate-45 absolute rounded bg-ihh-midnight-gray group-hover:bg-ihh-white py-[1px] rounded-lg px-1.5"></div>
						<div class="-rotate-45 rounded bg-ihh-midnight-gray group-hover:bg-ihh-white py-[1px] rounded-lg px-1.5"></div>
					</div>
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import Swal from 'sweetalert2'
export default {
	name: 'CartItemMobile',
	props: {
		item: {
			type: Object,
			required: true,
			validator: (item) => 'item' in item && 'quantity' in item,
		},
		XCoverProducts: {
			type: Array,
			default: () => [],
		},
	},
	methods: {
		emitReduceQty() {
			this.$emit('reduce-qty', this.item.variant?.id || this.item.item.id)
		},
		emitIncreaseQty() {
			if (this.item?.quantity < this.currentStock) {
				this.$emit('increase-qty', this.item.variant?.id || this.item.item.id)
			} else {
				Swal.fire({
					icon: 'warning',
					title: `Only ${this.currentStock} left in stock!`,
					text: `"${this.item?.item?.name}" has low stock.`,
					confirmButtonColor: '#e59789',
				})
			}
		},
		emitRemoveItem() {
			this.$emit('remove-item', this.item.variant?.id || this.item.item.id)
		},
	},
	computed: {
		currentPrice() {
			// If no variant or quantity is invalid, return the regular price
			if (!this.item.variant?.currentPrice || !this.item.quantity || this.item.quantity <= 0) {
				return this.item.variant?.currentPrice || this.item.item?.currentPrice
			}

			// If no bulk pricing rules available, return the regular price
			if (!this.item.item?.bulkPricingRules || this.item.item.bulkPricingRules.length === 0) {
				return this.item.variant?.currentPrice || this.item.item?.currentPrice
			}

			// Find the applicable bulk pricing rule based on quantity
			const applicableRule = this.item.item.bulkPricingRules
				.filter((rule) => this.item.quantity >= rule.quantityMin && (rule.quantityMax === 0 || this.item.quantity <= rule.quantityMax))
				.sort((a, b) => b.quantityMin - a.quantityMin)[0] // Get the rule with the highest min quantity that applies

			// If no applicable rule found, return the regular price
			if (!applicableRule) {
				return this.item.variant?.currentPrice || this.item.item?.currentPrice
			}

			// Base price to apply discount to
			const basePrice = this.item.variant?.currentPrice || this.item.item?.currentPrice

			// Calculate price based on the rule type
			switch (applicableRule.type) {
				case 'price':
					// Price adjustment per product (subtract the amount from the original price)
					return basePrice - applicableRule.amount
				case 'percent':
					// Percentage discount (apply percentage reduction to original price)
					return basePrice * (1 - applicableRule.amount / 100)
				case 'fixed':
					// Fixed price regardless of original price
					return applicableRule.amount
				default:
					return basePrice
			}
		},
		formattedPrice() {
			return this.currentPrice?.toFixed(2)
		},
		currentStock() {
			const product = this.item?.item
			const variant = this.item?.variant
			if (product?.inventoryTracking == 'none') return 500
			if (product?.inventoryTracking == 'variant') {
				return variant?.inventoryLevel
			} else if (product?.inventoryTracking == 'product') {
				return product?.inventoryLevel
			}
		},
		stockAvailable() {
			if (this.currentStock > this.item?.quantity) {
				return true
			} else {
				return false
			}
		},
	},
}
</script>
